// @ts-nocheck — ported from app/web (noUncheckedIndexedAccess)
/**
 * Binary decoder for the streaming-preview wire format (see app/wire/encoder.go).
 *
 * Frame envelope: [u8 type][u16 payload_length LE][payload].
 * All multi-byte values little-endian; strings u16 length + UTF-8;
 * uvarint = unsigned LEB128; na = NaN.
 */

export const FRAME_META = 0x01;
export const FRAME_BAR = 0x02;
export const FRAME_SEC_BAR = 0x03;
export const FRAME_TRADE = 0x04;
export const FRAME_DRAWING = 0x05;
export const FRAME_LOG = 0x06;
export const FRAME_SHAPE = 0x07;
export const FRAME_HISTORICAL_DONE = 0x08;
export const FRAME_DONE = 0x09;
export const FRAME_FILL = 0x0A;
export const FRAME_COMPRESSED = 0x0B;

export interface MetaSecurity {
	symbol: string;
	timeframeMs: number;
	gaps: boolean;
	calcBarsCount: number;
}

export interface Meta {
	symbol: string;
	timeframeMs: number;
	warmupBars: number;
	qtyType: number; // 0 = fixed cash, 1 = percent of equity
	qtyValue: number;
	feeRate: number;
	stepSize: number;
	marginLong: number;
	marginShort: number;
	hlines: { title: string; price: number; color: number }[];
	fills: { title: string; left: number; right: number; isPlot: boolean; color: number }[];
	plots: string[];
	barColors: string[];
	bgColors: string[];
	shapes: string[];
	chars: string[];
	arrows: string[];
	alerts: string[];
	securities: MetaSecurity[];
	// v2 scale fields (spec version 2): price tick for the v2 bar payload
	// (0 = v1 bars), informational volume decimals.
	priceTick: number;
	volumeDecimals: number;
}

export interface Bar {
	time: number; // absolute unix ms
	open: number;
	high: number;
	low: number;
	close: number;
	volume: number;
	equity: number; // NaN during warmup
	barColors: number[]; // per slot, 0 = none
	bgColors: number[];
	alerts: number; // bitmask
	plots: Float32Array; // per meta plot order, NaN = na — copy immediately
}

export type SecBarEvent =
	| { sec: number; gap: false; time: number; open: number; high: number; low: number; close: number; volume: number }
	| { sec: number; gap: true };

export type Trade =
	| {
			kind: 'order';
			barIndex: number;
			action: number; // 1 = buy, 2 = sell
			price?: number;
			takeProfit?: number;
			tpTrailOffset?: number;
			stopLoss?: number;
			slTrailOffset?: number;
			qty?: number;
			reduce: boolean;
			positionSideShort: boolean;
			comment: string;
	  }
	| {
			kind: 'close';
			barIndex: number;
			typeId: string;
			comment: string;
			qty?: number;
	  }
	| {
			kind: 'setStopLoss';
			barIndex: number;
			stopLoss?: number;
			takeProfit?: number;
			slTrailOffset?: number;
			slTrailPercent?: number;
			slQty?: number;
			tpQty?: number;
			comment: string;
	  };

export interface DrawingEvent {
	eventKind: number; // 0 = created, 1 = updated, 2 = deleted
	id: number;
	objKind?: number; // 0 = line, 1 = box, 2 = label
	xloc?: number; // 0 = bar_index, 1 = bar_time
	style?: number;
	size?: number;
	extend?: number;
	x1?: number;
	y1?: number;
	x2?: number;
	y2?: number;
	color?: number;
	color2?: number;
	text?: string;
}

export interface LogEntry {
	level: number; // 0 = info, 1 = warn, 2 = error
	barIndex: number;
	message: string;
}

export interface ShapeEvent {
	slotKind: number; // 0 = shape, 1 = char, 2 = arrow
	slotIndex: number;
	barIndex: number;
	value?: number;
	style?: number;
	location?: number;
	size?: number;
	color?: number;
	color2?: number;
	label?: string;
	character?: string;
}

export interface Fill {
	kind: 'opened' | 'closed';
	time: number; // absolute unix ms
	price: number;
	qty: number; // signed; short < 0
	fee: number;
}

export type StreamEvent =
	| { t: 'meta'; meta: Meta }
	| { t: 'bar'; bar: Bar }
	| { t: 'secBar'; ev: SecBarEvent }
	| { t: 'trade'; trade: Trade }
	| { t: 'fill'; fill: Fill }
	| { t: 'drawing'; ev: DrawingEvent }
	| { t: 'log'; entries: LogEntry[] }
	| { t: 'shape'; ev: ShapeEvent }
	| { t: 'historicalDone' }
	| { t: 'done'; warning: boolean; error: boolean; message: string };

const textDecoder = new TextDecoder();

class Reader {
	private view: DataView;
	private pos = 0;

	constructor(private bytes: Uint8Array) {
		this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
	}

	private need(n: number) {
		if (this.pos + n > this.bytes.byteLength) {
			throw new Error(`wire decode: read past end (${this.pos}+${n} > ${this.bytes.byteLength})`);
		}
	}

	u8(): number {
		this.need(1);
		return this.view.getUint8(this.pos++);
	}
	u16(): number {
		this.need(2);
		const v = this.view.getUint16(this.pos, true);
		this.pos += 2;
		return v;
	}
	u32(): number {
		this.need(4);
		const v = this.view.getUint32(this.pos, true);
		this.pos += 4;
		return v;
	}
	f32(): number {
		this.need(4);
		const v = this.view.getFloat32(this.pos, true);
		this.pos += 4;
		return v;
	}
	f64(): number {
		this.need(8);
		const v = this.view.getFloat64(this.pos, true);
		this.pos += 8;
		return v;
	}
	uvarint(): number {
		let v = 0;
		let shift = 0;
		for (;;) {
			const c = this.u8();
			// += not |= : the OR assignment coerces to int32 and corrupts
			// values above 2^31 (absolute unix ms times).
			v += (c & 0x7f) * Math.pow(2, shift);
			if ((c & 0x80) === 0) return v;
			shift += 7;
		}
	}
	str(): string {
		const n = this.u16();
		this.need(n);
		const s = textDecoder.decode(this.bytes.subarray(this.pos, this.pos + n));
		this.pos += n;
		return s;
	}
	/** Signed zigzag LEB128 (see appendZvarint in encoder.go). */
	zvarint(): number {
		const u = this.uvarint();
		// Values stay well under 2^53 for realistic deltas (prices in
		// tick units, volumes in 1e-6 units, times in ms).
		const v = Math.floor(u / 2);
		return u % 2 === 1 ? -v - 1 : v;
	}
	/** Raw f32 bits as u32 (for plot XOR runs). */
	u32bits(): number {
		this.need(4);
		const v = this.view.getUint32(this.pos, true);
		this.pos += 4;
		return v;
	}
}

/** Delta-chain state for v2 bar payloads. One instance per stream,
 * constructed after the meta frame (needs priceTick). */
export class BarDecodeState {
	prevTime = 0;
	prevDelta = 0;
	prevClose = 0; // tick units
	prevVolume = 0; // 1e-6 units
	plotPrev: number[] = []; // f32 bits per slot, NaN-initialized lazily

	constructor(readonly priceTick: number) {}
}

// v2 bar payload flags (see barFlagV2* in encoder.go).
const BAR_V2_FIRST = 1 << 0;
const BAR_V2_EQUITY_NAN = 1 << 1;
const BAR_V2_VOLUME_RAW = 1 << 2;
const BAR_V2_PRICES_RAW = 1 << 3;

// Canonical quiet-NaN f32 bits (both sides normalize NaN plots to this).
const NAN_BITS_32 = 0x7fc00000;

function decodeBarV2(r: Reader, st: BarDecodeState, plotCount: number): Bar {
	const td = r.zvarint();
	const flags = r.u8();

	// Absolute time resolved here; the returned bar carries the delta
	// from the previous bar (the accumulator's clock adds prevMainTime,
	// same contract as v1).
	const prevAbs = st.prevTime;
	let absTime: number;
	if (flags & BAR_V2_FIRST) {
		absTime = td;
		st.prevDelta = 0;
	} else {
		const delta = td + st.prevDelta;
		absTime = st.prevTime + delta;
		st.prevDelta = delta;
	}
	st.prevTime = absTime;
	const time = absTime - prevAbs;

	let open: number, high: number, low: number, close: number;
	if (flags & BAR_V2_PRICES_RAW) {
		open = r.f32();
		high = r.f32();
		low = r.f32();
		close = r.f32();
		// Re-anchor the close chain: quantize the raw f32 close to tick
		// units (mirrors the encoder's fallback anchoring).
		st.prevClose = Math.round(close / st.priceTick);
	} else {
		let o: number;
		if (flags & BAR_V2_FIRST) o = r.zvarint();
		else o = r.zvarint() + st.prevClose;
		const h = r.zvarint() + o;
		const l = r.zvarint() + o;
		const c = r.zvarint() + o;
		open = f32(o * st.priceTick);
		high = f32(h * st.priceTick);
		low = f32(l * st.priceTick);
		close = f32(c * st.priceTick);
		st.prevClose = c;
	}

	let volume: number;
	if (flags & BAR_V2_VOLUME_RAW) {
		volume = r.f64();
		st.prevVolume = Math.round(volume * 1e6);
	} else {
		let viv: number;
		if (flags & BAR_V2_FIRST) viv = r.zvarint();
		else viv = r.zvarint() + st.prevVolume;
		volume = viv / 1e6;
		st.prevVolume = viv;
	}

	const equity = flags & BAR_V2_EQUITY_NAN ? NaN : r.f64();

	const barColors = decodeMaskedColors(r);
	const bgColors = decodeMaskedColors(r);
	const alerts = r.uvarint();

	// Plot run records, one per slot in meta order.
	const plots = new Float32Array(plotCount);
	for (let slot = 0; slot < plotCount; slot++) {
		if (st.plotPrev.length <= slot) st.plotPrev[slot] = NAN_BITS_32;
		const kind = r.u8();
		switch (kind) {
			case 0x00: // unchanged
				plots[slot] = st.plotPrev[slot] === NAN_BITS_32 ? NaN : f32frombits(st.plotPrev[slot]);
				break;
			case 0x01: // NaN this bar
				plots[slot] = NaN;
				st.plotPrev[slot] = NAN_BITS_32;
				break;
			case 0x03: {
				// absolute bits
				const v = r.u32bits();
				plots[slot] = f32frombits(v);
				st.plotPrev[slot] = v;
				break;
			}
			case 0x02: {
				// XOR run: combo byte lead<<2|(n-1), then n bytes
				const combo = r.u8();
				const lead = combo >> 2;
				const n = (combo & 3) + 1;
				const trail = 4 - lead - n;
				let x = 0;
				for (let i = 0; i < n; i++) x += r.u8() * Math.pow(2, 8 * (i + trail));
				const v = x ^ st.plotPrev[slot];
				plots[slot] = f32frombits(v);
				st.plotPrev[slot] = v;
				break;
			}
			default:
				throw new Error('bad plot run kind: ' + kind);
		}
	}
	return { time, open, high, low, close, volume, equity, barColors, bgColors, alerts, plots };
}

/** f64 → f32 round-trip, matching Go's float32(v). */
function f32(v: number): number {
	const arr = new Float32Array(1);
	arr[0] = v;
	return arr[0];
}

/** f32 bits → value. */
function f32frombits(bits: number): number {
	const arr = new Float32Array(1);
	new Uint32Array(arr.buffer)[0] = bits;
	return arr[0];
}

/** Decode one frame payload into a typed event, or null for unknown types.
 * plotCount (from meta) is required for bar frames: the bar payload's plot
 * column length is not self-describing. barState (v2 streams) selects the
 * int/scale bar payload decoder; null decodes v1 bars. */
export function decodeFrame(type: number, payload: Uint8Array, plotCount = 0, barState: BarDecodeState | null = null): StreamEvent | null {
	const r = new Reader(payload);
	switch (type) {
		case FRAME_META:
			return { t: 'meta', meta: decodeMeta(r) };
		case FRAME_BAR:
			return { t: 'bar', bar: barState ? decodeBarV2(r, barState, plotCount) : decodeBar(r, plotCount) };
		case FRAME_SEC_BAR:
			return { t: 'secBar', ev: decodeSecBar(r) };
		case FRAME_TRADE:
			return { t: 'trade', trade: decodeTrade(r) };
		case FRAME_FILL:
			return { t: 'fill', fill: decodeFill(r) };
		case FRAME_DRAWING:
			return { t: 'drawing', ev: decodeDrawing(r) };
		case FRAME_LOG:
			return { t: 'log', entries: decodeLog(r) };
		case FRAME_SHAPE:
			return { t: 'shape', ev: decodeShape(r) };
		case FRAME_HISTORICAL_DONE:
			return { t: 'historicalDone' };
		case FRAME_DONE:
			return decodeDone(r);
		default:
			return null;
	}
}

function decodeMeta(r: Reader): Meta {
	const meta: Meta = {
		symbol: '',
		timeframeMs: 0,
		warmupBars: 0,
		qtyType: 0,
		qtyValue: 0,
		feeRate: 0,
		stepSize: 0,
		marginLong: 0,
		marginShort: 0,
		hlines: [],
		fills: [],
		plots: [],
		barColors: [],
		bgColors: [],
		shapes: [],
		chars: [],
		arrows: [],
		alerts: [],
		securities: [],
		priceTick: 0,
		volumeDecimals: 0
	};
	const version = r.u8();
	if (version !== 1 && version !== 2) throw new Error('unsupported spec version ' + version);
	meta.symbol = r.str();
	meta.timeframeMs = r.u32();
	meta.warmupBars = r.u32();
	meta.qtyType = r.u8();
	meta.qtyValue = r.f64();
	meta.feeRate = r.f64();
	meta.stepSize = r.f64();
	meta.marginLong = r.f64();
	meta.marginShort = r.f64();
	const nHl = r.u16();
	for (let i = 0; i < nHl; i++) meta.hlines.push({ title: r.str(), price: r.f64(), color: r.u32() });
	const nFills = r.u16();
	for (let i = 0; i < nFills; i++) {
		const title = r.str();
		const left = r.u16();
		const right = r.u16();
		const isPlot = r.u8() === 1;
		const color = r.u32();
		meta.fills.push({ title, left, right, isPlot, color });
	}
	for (const push of [(s: string) => meta.plots.push(s), (s: string) => meta.barColors.push(s), (s: string) => meta.bgColors.push(s), (s: string) => meta.shapes.push(s), (s: string) => meta.chars.push(s), (s: string) => meta.arrows.push(s), (s: string) => meta.alerts.push(s)]) {
		const n = r.u16();
		for (let i = 0; i < n; i++) push(r.str());
	}
	const nSec = r.u8();
	for (let i = 0; i < nSec; i++) {
		const symbol = r.str();
		const timeframeMs = r.u32();
		const gaps = r.u8() === 1;
		const calcBarsCount = r.u32();
		meta.securities.push({ symbol, timeframeMs, gaps, calcBarsCount });
	}
	if (version >= 2) {
		meta.priceTick = r.f64();
		meta.volumeDecimals = r.u8();
	}
	return meta;
}

function decodeBar(r: Reader, plotCount: number): Bar {
	const time = r.uvarint(); // delta resolved by the accumulator
	const open = r.f32();
	const high = r.f32();
	const low = r.f32();
	const close = r.f32();
	const volume = r.f64();
	const equity = r.f64();
	const barColors = decodeMaskedColors(r);
	const bgColors = decodeMaskedColors(r);
	const alerts = r.uvarint();
	// Plot column: f32 values in meta order, NaN = na. Direct typed-array
	// view over the payload — valid only for this event's synchronous
	// handling (the accumulator copies it immediately).
	const plots = new Float32Array(plotCount);
	for (let i = 0; i < plotCount; i++) plots[i] = r.f32();
	return { time, open, high, low, close, volume, equity, barColors, bgColors, alerts, plots };
}

function decodeMaskedColors(r: Reader): number[] {
	const mask = r.uvarint();
	const out: number[] = [];
	// Colors of the set bits, in bit order (mirrors appendMaskedColors).
	for (let bit = 0; bit < 32 && mask >>> bit !== 0; bit++) {
		if ((mask >>> bit) & 1) out.push(r.u32());
	}
	return out;
}

function decodeSecBar(r: Reader): SecBarEvent {
	const sec = r.u8();
	if (r.u8() === 1) return { sec, gap: true };
	const time = r.uvarint();
	const open = r.f32();
	const high = r.f32();
	const low = r.f32();
	const close = r.f32();
	const volume = r.f64();
	return { sec, gap: false, time, open, high, low, close, volume };
}

const ORDER_FLAG_PRICE = 1 << 0;
const ORDER_FLAG_TP = 1 << 1;
const ORDER_FLAG_TP_TRAIL = 1 << 2;
const ORDER_FLAG_SL = 1 << 3;
const ORDER_FLAG_SL_TRAIL = 1 << 4;
const ORDER_FLAG_QTY = 1 << 5;
const ORDER_FLAG_REDUCE = 1 << 6;
const ORDER_FLAG_POS_SHORT = 1 << 7;

function decodeTrade(r: Reader): Trade {
	const kind = r.u8();
	const barIndex = r.uvarint();
	if (kind === 0) {
		const action = r.u8();
		const flags = r.u8();
		const t: Trade = {
			kind: 'order',
			barIndex,
			action,
			reduce: (flags & ORDER_FLAG_REDUCE) !== 0,
			positionSideShort: (flags & ORDER_FLAG_POS_SHORT) !== 0,
			comment: ''
		};
		if (flags & ORDER_FLAG_PRICE) (t as any).price = r.f64();
		if (flags & ORDER_FLAG_TP) (t as any).takeProfit = r.f64();
		if (flags & ORDER_FLAG_TP_TRAIL) (t as any).tpTrailOffset = r.f64();
		if (flags & ORDER_FLAG_SL) (t as any).stopLoss = r.f64();
		if (flags & ORDER_FLAG_SL_TRAIL) (t as any).slTrailOffset = r.f64();
		if (flags & ORDER_FLAG_QTY) (t as any).qty = r.f64();
		t.comment = r.str();
		return t;
	} else if (kind === 1) {
		const typeId = r.str();
		const comment = r.str();
		const flags = r.u8();
		const t: Trade = { kind: 'close', barIndex, typeId, comment, qty: undefined };
		if (flags & 1) t.qty = r.f64();
		return t;
	}
	const flags = r.u8();
	const t: Trade = { kind: 'setStopLoss', barIndex, stopLoss: undefined, takeProfit: undefined, slTrailOffset: undefined, slTrailPercent: undefined, slQty: undefined, tpQty: undefined, comment: '' };
	if (flags & 1) t.stopLoss = r.f64();
	if (flags & 2) t.takeProfit = r.f64();
	if (flags & 4) t.slTrailOffset = r.f64();
	if (flags & 8) t.slTrailPercent = r.f64();
	if (flags & 16) t.slQty = r.f64();
	if (flags & 32) t.tpQty = r.f64();
	t.comment = r.str();
	return t;
}

function decodeFill(r: Reader): Fill {
	const kindByte = r.u8();
	return {
		kind: kindByte === 1 ? 'closed' : 'opened',
		time: r.uvarint(),
		price: r.f64(),
		qty: r.f64(),
		fee: r.f64()
	};
}

function decodeDrawing(r: Reader): DrawingEvent {
	const eventKind = r.u8();
	const id = r.uvarint();
	const ev: DrawingEvent = { eventKind, id };
	if (eventKind === 2) return ev; // deleted: id only
	ev.objKind = r.u8();
	ev.xloc = r.u8();
	ev.style = r.u32() | 0;
	ev.size = r.u32() | 0;
	ev.extend = r.u32() | 0;
	ev.x1 = r.f64();
	ev.y1 = r.f64();
	ev.x2 = r.f64();
	ev.y2 = r.f64();
	const flags = r.u8();
	if (flags & 1) ev.color = r.u32();
	if (flags & 2) ev.color2 = r.u32();
	ev.text = r.str();
	return ev;
}

function decodeLog(r: Reader): LogEntry[] {
	const n = r.u8();
	const entries: LogEntry[] = [];
	for (let i = 0; i < n; i++) {
		const level = r.u8();
		const barIndex = r.uvarint();
		const message = r.str();
		entries.push({ level, barIndex, message });
	}
	return entries;
}

function decodeShape(r: Reader): ShapeEvent {
	const slotKind = r.u8();
	const slotIndex = r.u8();
	const barIndex = r.uvarint();
	const flags = r.u8();
	const ev: ShapeEvent = { slotKind, slotIndex, barIndex };
	if (flags & 1) ev.value = r.f64();
	ev.style = r.u32() | 0;
	ev.location = r.u32() | 0;
	ev.size = r.u32() | 0;
	if (flags & 2) ev.color = r.u32();
	if (flags & 4) ev.color2 = r.u32();
	ev.label = r.str();
	ev.character = r.str();
	return ev;
}

function decodeDone(r: Reader): StreamEvent {
	const flags = r.u8();
	// The encoder omits the string when no flag is set (clean end).
	const message = flags !== 0 ? r.str() : '';
	return {
		t: 'done',
		warning: (flags & 1) !== 0,
		error: (flags & 2) !== 0,
		message
	};
}
